<template>
	<div class="w-100">
		<div class="card">
			<div class="card-header bg-primary text-white">
				<div class="card-title h5 m-0">{{ $tc("signing.pageTitle", 2) }}</div>
			</div>

			<div class="btn-toolbar justify-content-between mb-0 mt-3" role="toolbar">
				<div class="col-w"></div>
				<div class="input-group">
					<input
						v-model="searchQuery"
						v-on:keyup.enter.stop="massSearch"
						v-on:keyup.esc.stop="clearFilters"
						type="text"
						class="form-control"
						aria-label="Mass search input"
						aria-describedby="massSearch"
					/>
					<button class="btn btn-secondary" @click.stop="massSearch">
						<i class="fas fa-search"></i>
						{{ $t('buttons.search.btnText') }}
					</button>
					<button class="btn btn-secondary" @click.stop="clearFilters">
						<i class="fas fa-times"></i>
						{{ $t('buttons.clearFilters.btnText') }}
					</button>
				</div>
			</div>

			<div class="card-body">
				<div class="grid-wrapper">
					<kendo-datasource
						ref="datasource"
						:transport="transportSettings"
						:schema-model-fields="schemaModelFields"
						:schema-model-id="'id'"
						:schema-total="'total'"
						:schema-data="'data'"
						:page-size="defaultGridRows"
						:server-paging="true"
						:server-sorting="true"
						:server-filtering="true"
					/>

					<kendo-grid
						ref="signaturesGrid"
						:data-source-ref="'datasource'"
						:navigatable="gridOptions.navigatable"
						:editable="false"
						:filterable="gridOptions.filterable"
						:pageable="gridOptions.pageable"
						:reorderable="gridOptions.reorderable"
						:resizable="gridOptions.resizable"
						:groupable="gridOptions.groupable"
						:allow-copy="gridOptions.allowCopy"
						:column-menu="gridOptions.columnMenu"
						:sortable-mode="gridOptions.sortable.mode"
						:sortable-allow-unsort="gridOptions.sortable.allowUnsort"
						:sortable-show-indexes="gridOptions.sortable.showIndexes"
						:selectable="gridOptions.selectable"
						:scrollable="gridOptions.scrollable"
						:dataBound="onDatabound"
					>
                        <kendo-grid-column
                            :field="'id'"
                            :title="'Id'"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'title'"
                            :title="$t('signing.headers.title')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'statusName'"
                            :title="$t('signing.headers.status')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'creatorName'"
                            :title="$t('signing.headers.creator')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'signatoryName'"
                            :title="$t('signing.headers.signatories')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'deadline'"
                            :title="$t('signing.headers.deadline')"
                            :lockable="true"
                            :locked="false"
                            :format="customDateTimeFormat"
                        />
						<kendo-grid-column
                            :field="'archived'"
                            :title="$t('signing.headers.archived')"
                            :template="boolCellTemplate"
                        />
                        <kendo-grid-column
                            :template="btnsTemplate"
                            field="btnsColumn"
                            :title="$t('actions')"
                            :filterable="false"
                        />
					</kendo-grid>
				</div>
			</div>
		</div>
        <DisplayModal
            v-model="activeItem"
            :show.sync="showDisplay"
            @onClose="onDisplayClose"
        />
	</div>
</template>

<script>
import Vue from 'vue';
import DisplayModal from '@/components/signatures/display.modal';
import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
import signingService from '@/services/signing.service';
import { SigningStatus } from '@/models/enums';
import { mapGetters } from 'vuex'

export default {
	components: {
		DisplayModal
	},
	data() {
		return {
			defaultGridRows: this.$store.getters.defaultGridRows,
            activeItem: null,
            activeItemId: null,
			showDisplay: false,
			searchQuery: '',
			schemaModelFields: {
                id: { name: 'id', type: 'number', nullable: false },
                title: { name: 'title', type: 'string', nullable: true },
                statusId: { name: 'statusId', type: 'string', nullable: true },
                statusName: { name: 'statusName', type: 'string', nullable: true },
                statusNameEn: { name: 'statusNameEn', type: 'string', nullable: true },
                creatorName: { name: 'creatorName', type: 'string', nullable: true },
                creatorNameEn: { name: 'creatorNameEn', type: 'string', nullable: true },
                signatoryId: { name: 'signatoryId', type: 'string', nullable: true },
                signatoryName: { name: 'signatoryName', type: 'string', nullable: true },
                signatoryNameEn: { name: 'signatoryNameEn', type: 'string', nullable: true },
                deadline: { name: 'deadline', type: 'date', nullable: true },
                relatedTaskId: { name: 'relatedTaskId', type: 'number', nullable: true },
                relatedCoordinationId: { name: 'relatedCoordinationId', type: 'number', nullable: true },
                archived: { name: 'archived', type: 'boolean', nullable: true },
            },
            transportSettings: {
				read: {
					url: this.$store.getters.baseUrl + '/api/signing/all',
					dataType: 'json',
					type: 'POST',
					contentType: 'application/json',
					beforeSend: this.beforeSend
				},
				parameterMap: this.parameterMap
			},
    };
	},
	methods: {
		onDatabound(e) {
            var items = e.sender.items();
            const that = this;
            items.each(function (idx, item) {
                let dataItem = e.sender.dataItem(item);
                if (dataItem.statusId === SigningStatus.started) {
                    if (dataItem.signatoryId === that.$store.getters.userId) {
                        $(item).addClass('left-border-blue');
                    }
                }
            });
        },
		parameterMap: function(options, operation) {
			if (operation !== 'read' && options.models) {
				return { models: kendo.stringify(options.models) };
			} else {
				// Викаме POST action за листване на номенклатурите и трябва да сериализираме
				// options. Така се предава информация за take, skip , page и списък с обекти
				// описващи сортирането
				// this.saveState();
				return kendo.stringify(options);
			}
		},
        beforeSend: function(req) {
            if (this.authToken) {
                req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
            }
        },
		boolCellTemplate: function(dataItem) {
			let templateHtml =
				'<div class="text-center"><span class="badge ' +
				(dataItem.archived ? 'badge-success' : 'badge-secondary') +
				'">' +
				kendo.htmlEncode(dataItem.archived ? this.$t('yes') : this.$t('no')) +
				'</span></div>';

			return templateHtml;
		},
		btnsTemplate: function(e) {
			return {
				template: Vue.component(BtnsTemplate.name, BtnsTemplate),
				templateArgs: {
					...e,
					btns: [
						{
							name: 'details',
							text: '',
							tooltip: this.$t('buttons.details.tooltipText'),
							class: 'btn btn-info',
							icon: 'fas fa-eye',
							show: this.hasReadRights,
							clickHandler: this.showDetailsModal
						},
						{
							name: 'archive',
							text: '',
							tooltip: this.$t('buttons.archive.tooltipText'),
							class: 'btn btn-danger',
							icon: 'fas fa-archive',
							show:
								!e.archived &&
								this.hasEditRights &&
								(e.statusId === SigningStatus.completed || e.statusId === SigningStatus.canceled),
							clickHandler: this.onArchiveSigning
						},
						{
							name: 'unarchive',
							text: '',
							tooltip: this.$t('buttons.unarchive.tooltipText'),
							class: 'btn btn-danger',
							icon: 'fas fa-archive',
							show: e.archived && this.hasEditRights,
							clickHandler: this.onUnarchiveSigning
						}
					]
				}
			};
		},
		showDetailsModal(item) {
			this.activeItem = { ...item };
            this.showDisplay = true;
		},
		onDisplayClose() {
			this.showDisplay = false;
			this.activeItem = null;
            this.activeItemId = null;
		},
		onUnarchiveSigning(e) {
			this.activeItemId = e.id;
			this.$vs.dialog({
				type: 'confirm',
				color: 'success',
				title: this.$t('buttons.unarchive.tooltipText'),
				text: this.$t('signing.unarchiveMessage'),
				accept: this.unarchiveSigning,
				acceptText: this.$t('buttons.ok'),
				cancelText: this.$t('buttons.cancel'),
				cancel: () => (this.activeItemId = null)
			});
		},
		onArchiveSigning(e) {
			this.activeItemId = e.id;
			this.$vs.dialog({
				type: 'confirm',
				color: 'success',
				title: this.$t('buttons.archive.tooltipText'),
				text: this.$t('signing.archiveMessage'),
				accept: this.archiveSigning,
				acceptText: this.$t('buttons.ok'),
				cancelText: this.$t('buttons.cancel'),
				cancel: () => (this.activeItemId = null)
			});
		},
		unarchiveSigning() {
			signingService
				.unarchive(this.activeItemId)
				.then(() => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('updateSuccess'),
						text: ''
					});
					this.refresh();
				})
				.catch(err =>
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$helpers.errorText(err)
					})
				)
				.then(() => (this.activeItemId = null));
		},
		archiveSigning() {
			signingService
				.archive(this.activeItemId)
				.then(() => {
					this.$vs.notify({
						color: 'success',
						title: this.$t('updateSuccess'),
						text: ''
					});
					this.refresh();
				})
				.catch(err =>
					this.$vs.notify({
						color: 'danger',
						title: this.$t('notifications.error'),
						text: this.$helpers.errorText(err)
					})
				)
				.then(() => (this.activeItemId = null));
		},
		refresh() {
			this.$refs['signaturesGrid'].kendoWidget().dataSource.read();
		},
		clearFilters() {
			this.searchQuery = '';
			const grid = this.$refs.signaturesGrid.kendoWidget();
			if (grid) {
				grid.dataSource.sort({});
				grid.dataSource.page(1);
				grid.dataSource.aggregate([]);
				grid.dataSource.filter({});
			}
		},
		massSearch() {
			if (!this.searchQuery) {
				this.$vs.notify({
                    title: this.$t('buttons.search.btnText'),
                    text: this.$t('emptySearchFilter'),
					color: 'warning',
					time: this.$store.getters.notificationDuration
				});

				return;
			}

			let grid = this.$refs.signaturesGrid.kendoWidget();
			if (grid) {
				let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
				if (filter) {
					grid.dataSource.filter(filter);
				}
			}
		}
	},
	computed: {
        ...mapGetters(['gridOptions', 'authToken', 'dateTimeFormat']),
        customDateTimeFormat() {
            return `{0: ${this.dateTimeFormat}}`;
        },
		hasReadRights() {
			return this.$store.getters.claims.tasks.read;
		},
		hasEditRights() {
			return this.$store.getters.claims.tasks.create || this.$store.getters.claims.tasks.update;
		}
	}
};
</script>

<style>
</style>
